/**
 * BuyerEdiPanel · Feb 2026 · Iter 209 (Phase B)
 * ─────────────────────────────────────────────────────────
 * Per-buyer EDI trading-partner configuration + document flow.
 * Used for retailer relationships (Macy's, Nordstrom, TJX, Target
 * Plus, Bloomingdale's, JCPenney …) where orders arrive as EDI 850
 * files instead of via a marketplace API.
 *
 * Two states:
 *   1. Empty · settings form (trading partner id, vendor code, VAN
 *      provider, SFTP creds). Save → EDI enabled for this buyer.
 *   2. Enabled · shows current settings, an "Ingest 850" panel that
 *      accepts pasted EDI text or uses a generated sample, and the
 *      resulting orders' EDI history (855/856/810 emitters).
 *
 * Kept intentionally lean — the real-world upgrade path is a Phase-2
 * SPS Commerce / DiCentral SFTP poller that ingests 850s
 * automatically, without changing this UI at all.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../../../lib/api";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Label } from "../../../../components/ui/label";
import { Textarea } from "../../../../components/ui/textarea";
import {
  FileText, Zap, Download, Upload, Send, ShieldCheck, Sparkles, Loader2, Copy,
  Building2, Truck, Receipt, Clock, Radio, Activity, PackageCheck, Lock,
} from "lucide-react";

const VAN_PROVIDERS = [
  { id: "manual",       label: "Manual · paste 850s" },
  { id: "sps_commerce", label: "SPS Commerce (Phase 2)" },
  { id: "dicentral",    label: "DiCentral (Phase 2)" },
  { id: "truecommerce", label: "TrueCommerce (Phase 2)" },
];

const KNOWN_PARTNERS = [
  "MACYS", "NORDSTROM", "BLOOMINGDALES", "TJX", "TJMAXX", "TARGETPLUS",
  "JCPENNEY", "KOHLS", "SAKS", "NEIMANMARCUS", "DILLARDS", "BELK",
];

export default function BuyerEdiPanel({ buyer }) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ingestText, setIngestText] = useState("");
  const [ingesting, setIngesting] = useState(false);
  const [recentOrders, setRecentOrders] = useState([]);

  // Form state (uncontrolled until edited)
  const [form, setForm] = useState({
    trading_partner_id: "",
    vendor_code: "",
    van_provider: "manual",
    sftp_host: "",
    sftp_user: "",
    inbox_path: "",
    outbox_path: "",
    notes: "",
  });

  const load = () => {
    if (!buyer?.id) return;
    setLoading(true);
    Promise.all([
      api.get(`/edi/buyers/${buyer.id}/settings`).then(r => r.data || {}),
      api.get(`/dropship/orders?buyer_id=${buyer.id}&limit=25`).then(r => r.data || [])
         .catch(() => []),
    ])
      .then(([s, orders]) => {
        setSettings(s);
        if (s?.trading_partner_id) {
          setForm({
            trading_partner_id: s.trading_partner_id || "",
            vendor_code:        s.vendor_code || "",
            van_provider:       s.van_provider || "manual",
            sftp_host:          s.sftp_host || "",
            sftp_user:          s.sftp_user || "",
            inbox_path:         s.inbox_path || "",
            outbox_path:        s.outbox_path || "",
            notes:              s.notes || "",
          });
        }
        // Filter to EDI-sourced orders only
        setRecentOrders((orders || []).filter(o => o.edi_po_number).slice(0, 10));
      })
      .catch(() => toast.error("Could not load EDI settings"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [buyer?.id]); // load is stable

  const save = async () => {
    if (!form.trading_partner_id.trim()) {
      toast.error("Trading partner id is required");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/edi/buyers/${buyer.id}/settings`, form);
      toast.success("EDI settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const generateSample = async () => {
    try {
      const r = await api.post(`/edi/buyers/${buyer.id}/sample-850`);
      setIngestText(r.data?.edi_text || "");
      toast.success("Sample 850 generated · click Ingest to import");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate sample");
    }
  };

  const ingest = async () => {
    if (!ingestText.trim()) return;
    setIngesting(true);
    try {
      const r = await api.post(`/edi/buyers/${buyer.id}/ingest-850`, {
        edi_text: ingestText,
      });
      const d = r.data;
      if (d.duplicate) {
        toast(`PO ${d.po_number} already ingested as ${d.code}`, { icon: "ℹ️" });
      } else {
        toast.success(`Ingested PO ${d.po_number} → ${d.code} (${d.line_count} lines)`);
      }
      setIngestText("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ingest failed");
    } finally { setIngesting(false); }
  };

  const emit = async (orderId, docType) => {
    try {
      const r = await api.post(`/edi/orders/${orderId}/${docType}`);
      // Trigger a browser download of the raw X12 text.
      const blob = new Blob([r.data?.edi_text || ""], { type: "text/plain" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${docType.toUpperCase()}-${orderId.slice(0, 8)}.edi`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`${docType.toUpperCase()} generated (${r.data?.byte_len || 0} bytes)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not generate ${docType.toUpperCase()}`);
    }
  };

  if (!buyer) return null;

  return (
    <div data-testid="edi-panel" className="space-y-4">
      {loading ? (
        <div className="p-6 grid place-items-center text-foreground/40">
          <Loader2 className="w-4 h-4 animate-spin"/>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Settings form */}
          <div>
            <div className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/50 mb-2 flex items-center gap-1.5">
              <Building2 className="w-3 h-3"/> Trading partner
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-[11px] text-foreground/60">Trading partner id (ISA sender)</Label>
                <Input
                  data-testid="edi-tp-id"
                  list="edi-known-partners"
                  placeholder="MACYS"
                  value={form.trading_partner_id}
                  onChange={e => setForm({...form, trading_partner_id: e.target.value.toUpperCase()})}
                />
                <datalist id="edi-known-partners">
                  {KNOWN_PARTNERS.map(p => <option key={p} value={p}/>)}
                </datalist>
              </div>
              <div>
                <Label className="text-[11px] text-foreground/60">Your vendor code (REF*IA)</Label>
                <Input
                  data-testid="edi-vendor-code"
                  placeholder="V54321"
                  value={form.vendor_code}
                  onChange={e => setForm({...form, vendor_code: e.target.value})}
                />
              </div>
              <div>
                <Label className="text-[11px] text-foreground/60">VAN provider</Label>
                <select
                  data-testid="edi-van"
                  className="w-full mt-1 h-9 rounded-md border border-border bg-background px-2 text-[13px]"
                  value={form.van_provider}
                  onChange={e => setForm({...form, van_provider: e.target.value})}
                >
                  {VAN_PROVIDERS.map(v => <option key={v.id} value={v.id}>{v.label}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-[11px] text-foreground/60">SFTP host (optional)</Label>
                <Input
                  data-testid="edi-sftp-host"
                  placeholder="sftp.spscommerce.com"
                  value={form.sftp_host}
                  onChange={e => setForm({...form, sftp_host: e.target.value})}
                />
              </div>
            </div>
            <div className="mt-3">
              <Button onClick={save} disabled={saving} data-testid="edi-save"
                      className="rounded-full h-8 px-3 text-[12px]">
                {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <ShieldCheck className="w-3.5 h-3.5 mr-1"/>}
                {settings?.enabled ? "Update" : "Enable EDI"}
              </Button>
              {settings?.enabled && (
                <span data-testid="edi-status-enabled"
                      className="ml-3 text-[10.5px] mono uppercase tracking-[0.14em] text-emerald-700">
                  ● EDI enabled · {settings?.orders_ingested || 0} orders ingested
                </span>
              )}
            </div>
          </div>

          {/* Ingest 850 */}
          {settings?.enabled && (
            <div className="rounded-lg border border-border bg-secondary/20 p-4">
              <div className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/50 mb-2 flex items-center gap-1.5">
                <Upload className="w-3 h-3"/> Ingest EDI 850
              </div>
              <p className="text-[11.5px] text-foreground/60 mb-2">
                Paste the raw 850 you received from your VAN&apos;s inbox. We&apos;ll parse the PO,
                create the dropship order, and stamp the PO number for reconciliation.
              </p>
              <Textarea
                data-testid="edi-850-text"
                rows={5}
                placeholder="ISA*00*          *00*          *ZZ*MACYS          *ZZ*VERQOSELLER01  *..."
                value={ingestText}
                onChange={e => setIngestText(e.target.value)}
                className="mono text-[11px]"
              />
              <div className="flex items-center gap-2 mt-3">
                <Button onClick={ingest} disabled={!ingestText.trim() || ingesting}
                        data-testid="edi-ingest-submit"
                        className="rounded-full h-8 px-3 text-[12px]">
                  {ingesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin"/> : <Send className="w-3.5 h-3.5 mr-1"/>}
                  Ingest 850
                </Button>
                <Button variant="outline" onClick={generateSample}
                        data-testid="edi-sample-generate"
                        className="rounded-full h-8 px-3 text-[12px]">
                  <Sparkles className="w-3.5 h-3.5 mr-1"/>Generate sample
                </Button>
              </div>
            </div>
          )}

          {/* Recent EDI orders */}
          {settings?.enabled && recentOrders.length > 0 && (
            <div>
              <div className="text-[11.5px] mono uppercase tracking-[0.14em] text-foreground/50 mb-2 flex items-center gap-1.5">
                <Receipt className="w-3 h-3"/> Recent EDI orders · {recentOrders.length}
              </div>
              <div className="rounded-lg border border-border divide-y divide-border">
                {recentOrders.map(o => (
                  <div key={o.id} data-testid={`edi-order-row-${o.id}`}
                       className="flex items-center gap-3 px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="text-[13px] font-medium truncate">{o.code}</div>
                      <div className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/50">
                        PO {o.edi_po_number} · {o.lines?.length || 1} line{(o.lines?.length || 1) === 1 ? "" : "s"}
                      </div>
                    </div>
                    <button
                      onClick={() => emit(o.id, "855")}
                      title="Generate PO Acknowledgement (855)"
                      data-testid={`edi-emit-855-${o.id}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary/50 inline-flex items-center gap-1"
                    >
                      <ShieldCheck className="w-3 h-3"/>855
                    </button>
                    <button
                      onClick={() => emit(o.id, "856")}
                      title="Generate ASN (856)"
                      data-testid={`edi-emit-856-${o.id}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary/50 inline-flex items-center gap-1"
                    >
                      <Truck className="w-3 h-3"/>856
                    </button>
                    <button
                      onClick={() => emit(o.id, "810")}
                      title="Generate Invoice (810)"
                      data-testid={`edi-emit-810-${o.id}`}
                      className="text-[10.5px] mono uppercase tracking-[0.14em] rounded-full h-7 px-2.5 border border-border hover:bg-secondary/50 inline-flex items-center gap-1"
                    >
                      <Receipt className="w-3 h-3"/>810
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Iter 209 · Phase 2 EDI · Coming Soon teaser panel */}
          {settings?.enabled && (
            <div
              data-testid="edi-phase2-coming-soon"
              className="rounded-lg border border-dashed border-violet-300 bg-gradient-to-br from-violet-50/50 to-indigo-50/40 p-4"
            >
              <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="text-[11.5px] mono uppercase tracking-[0.14em] text-violet-800 font-semibold">
                      Phase 2 · EDI Automation
                    </div>
                    <span className="text-[9.5px] mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full bg-violet-600 text-white">
                      Coming soon
                    </span>
                  </div>
                  <p className="text-[11.5px] text-foreground/60 mt-1 max-w-2xl">
                    Skip the copy-paste. When Phase 2 ships, 850s land automatically from your VAN,
                    855s / 856s / 810s go out the door on ship + invoice events, and every retailer&apos;s
                    quirks (SSCC labels, cutoff times, mapping profiles) are handled declaratively.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-2.5">
                {[
                  { icon: Radio, title: "Automated SFTP polling",
                    body: "SPS Commerce · DiCentral · TrueCommerce. 850s pulled every 5 min, 855 dropped straight back in the outbox." },
                  { icon: PackageCheck, title: "Auto-send 856 & 810",
                    body: "When you ship, we push the ASN. When we invoice, we push the 810. No button clicks." },
                  { icon: Activity, title: "Activity log & retries",
                    body: "Timeline of every inbound/outbound event with exponential backoff on transient failures + alerts on parse errors." },
                  { icon: Lock, title: "Retailer compliance packs",
                    body: "Declarative profiles for Macy's, Nordstrom, TJX, Target Plus — SSCC labels, cutoff times, mandatory qualifiers." },
                ].map((f, i) => (
                  <div key={i}
                    data-testid={`edi-phase2-feature-${i}`}
                    className="rounded-md border border-violet-200/60 bg-white/70 backdrop-blur px-3 py-2.5 flex items-start gap-2.5"
                  >
                    <div className="w-7 h-7 rounded-md bg-violet-100 text-violet-700 grid place-items-center shrink-0">
                      <f.icon className="w-3.5 h-3.5"/>
                    </div>
                    <div className="min-w-0">
                      <div className="text-[12.5px] font-medium truncate">{f.title}</div>
                      <div className="text-[11px] text-foreground/55 mt-0.5">{f.body}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button
                  data-testid="edi-phase2-notify-cta"
                  onClick={() => toast.success("You're on the list — we'll ping you when Phase 2 ships.")}
                  className="text-[11.5px] font-medium rounded-full h-8 px-3 bg-violet-600 text-white hover:bg-violet-700 inline-flex items-center gap-1.5"
                >
                  <Clock className="w-3.5 h-3.5"/>Notify me when live
                </button>
                <span className="text-[10.5px] mono uppercase tracking-[0.14em] text-foreground/40">
                  Roadmap · Q2 2026
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
